import tailwindTypography from "@tailwindcss/typography";
import containerQueries from "@tailwindcss/container-queries";

export default {
  darkMode: "class", // or 'media' or 'class'
  prefix: "tw-",
  content: [
    "./resources/frontend/index.html",
    "./resources/frontend/src/**/*.{vue,js,ts,jsx,tsx}",
  ],
  // Use safelist to allow dynamic classes, just in time compiler
  safelist: [
    "!tw-bg-info",
    "!tw-bg-warning",
    "!tw-bg-negative",
    "!tw-bg-positive",
    "tw-bg-info/15",
    "tw-bg-info/20",
    "tw-bg-warning/15",
    "tw-bg-warning/20",
    "tw-bg-negative",
    "tw-bg-negative/20",
    "tw-bg-positive/15",
    "tw-bg-positive/20",
    "!tw-bg-positive/5",
    "!tw-border-positive/65",
    "!tw-bg-warning/5",
    "!tw-border-warning/65",
    "!tw-bg-negative/5",
    "tw-bg-negative/15",
    "!tw-border-negative/65",
    "tw-overflow-hidden",
    "tw-opacity-70",
    "tw-ml-auto",
  ],
  theme: {
    container: {
      screens: {
        "2xl": "1400px",
      },
    },
    // Override default shadows with Figma design system tokens
    boxShadow: {
      none: "none",
      xs: "var(--shadow-xxsmall)",
      sm: "var(--shadow-xsmall)",
      DEFAULT: "var(--shadow-small)",
      md: "var(--shadow-medium)",
      lg: "var(--shadow-large)",
      xl: "var(--shadow-xlarge)",
    },
    extend: {
      fontFamily: {
        heading: ["Inter", "sans-serif"],
        body: ["Inter", "sans-serif"],
      },
      fontSize: {
        "6xl": ["var(--6xl-font-size)", "var(--6xl-line-height)"],
        "5xl": ["var(--5xl-font-size)", "var(--5xl-line-height)"],
        "4xl": ["var(--4xl-font-size)", "var(--4xl-line-height)"],
        "3xl": ["var(--3xl-font-size)", "var(--3xl-line-height)"],
        "2xl": ["var(--2xl-font-size)", "var(--2xl-line-height)"],
        xl: ["var(--xl-font-size)", "var(--xl-line-height)"],
        lg: ["var(--lg-font-size)", "var(--lg-line-height)"],
        base: ["var(--base-font-size)", "var(--base-line-height)"],
        sm: ["var(--sm-font-size)", "var(--sm-line-height)"],
        "2xs": ["var(--2xs-font-size)", "var(--2xs-line-height)"],
        "3xs": ["var(--3xs-font-size)", "var(--3xs-line-height)"],
      },
      colors: {
        // Primary maps to aqua palette for shade access (tw-bg-primary-100, etc.)
        primary: {
          DEFAULT: "rgb(var(--color-primary))",
          50: "rgb(var(--color-aqua-100))",
          100: "rgb(var(--color-aqua-100))",
          200: "rgb(var(--color-aqua-200))",
          300: "rgb(var(--color-aqua-300))",
          400: "rgb(var(--color-aqua-400))",
          500: "rgb(var(--color-aqua-500))",
          600: "rgb(var(--color-aqua-600))",
          700: "rgb(var(--color-aqua-700))",
          800: "rgb(var(--color-aqua-800))",
          900: "rgb(var(--color-aqua-900))",
        },
        // Legacy aliases
        secondary: {
          DEFAULT: "rgb(var(--color-red-500))",
          50: "rgb(var(--color-red-100))",
          100: "rgb(var(--color-red-100))",
          200: "rgb(var(--color-red-200))",
          300: "rgb(var(--color-red-300))",
          400: "rgb(var(--color-red-400))",
          500: "rgb(var(--color-red-500))",
          600: "rgb(var(--color-red-600))",
        },
        tertiary: {
          DEFAULT: "rgb(var(--color-aqua-500))",
          50: "rgb(var(--color-aqua-100))",
          100: "rgb(var(--color-aqua-100))",
          200: "rgb(var(--color-aqua-200))",
          300: "rgb(var(--color-aqua-300))",
          400: "rgb(var(--color-aqua-400))",
          500: "rgb(var(--color-aqua-500))",
          600: "rgb(var(--color-aqua-600))",
          700: "rgb(var(--color-aqua-700))",
          800: "rgb(var(--color-aqua-800))",
          900: "rgb(var(--color-aqua-900))",
        },

        info: "rgb(var(--color-info))",
        warning: "rgb(var(--color-warning))",
        negative: "rgb(var(--color-negative))",
        positive: "rgb(var(--color-positive))",

        // Text
        text: { default: "rgb(var(--color-text-body))" },

        // Alerts
        alert: {
          info: "rgb(var(--color-alert-info))",
          positive: "rgb(var(--color-alert-positive))",
          warning: "rgb(var(--color-alert-warning))",
          negative: "rgb(var(--color-alert-negative))",
        },

        // Meetings
        "meeting-student": "rgb(var(--color-meeting-student))",
        "meeting-transition": "rgb(var(--color-meeting-transition))",
        "meeting-group": "rgb(var(--color-meeting-group))",

        // Borders
        line: "rgb(var(--color-line))",

        // Grid
        "grid-current-week-indicator":
          "rgb(var(--color-grid-current-week-indicator))",

        // Color palettes — Figma design system values override Tailwind defaults
        // Shades not in Figma (50, 700-950) fall through to Tailwind defaults
        grey: {
          50: "rgb(var(--color-grey-50))",
          100: "rgb(var(--color-grey-100))",
          200: "rgb(var(--color-grey-200))",
          300: "rgb(var(--color-grey-300))",
          400: "rgb(var(--color-grey-400))",
          500: "rgb(var(--color-grey-500))",
          600: "rgb(var(--color-grey-600))",
          700: "rgb(var(--color-grey-700))",
          800: "rgb(var(--color-grey-800))",
          900: "rgb(var(--color-grey-900))",
        },
        // Alias: tw-*-gray-* works same as tw-*-grey-*
        gray: {
          50: "rgb(var(--color-grey-50))",
          100: "rgb(var(--color-grey-100))",
          200: "rgb(var(--color-grey-200))",
          300: "rgb(var(--color-grey-300))",
          400: "rgb(var(--color-grey-400))",
          500: "rgb(var(--color-grey-500))",
          600: "rgb(var(--color-grey-600))",
          700: "rgb(var(--color-grey-700))",
          800: "rgb(var(--color-grey-800))",
          900: "rgb(var(--color-grey-900))",
        },

        // Aqua palette
        aqua: {
          100: "rgb(var(--color-aqua-100))",
          200: "rgb(var(--color-aqua-200))",
          300: "rgb(var(--color-aqua-300))",
          400: "rgb(var(--color-aqua-400))",
          500: "rgb(var(--color-aqua-500))",
          600: "rgb(var(--color-aqua-600))",
          700: "rgb(var(--color-aqua-700))",
          800: "rgb(var(--color-aqua-800))",
          900: "rgb(var(--color-aqua-900))",
        },

        // Red palette (Figma 100-600, Tailwind defaults for 50, 700-950)
        red: {
          50: "#fef2f2",
          100: "rgb(var(--color-red-100))",
          200: "rgb(var(--color-red-200))",
          300: "rgb(var(--color-red-300))",
          400: "rgb(var(--color-red-400))",
          500: "rgb(var(--color-red-500))",
          600: "rgb(var(--color-red-600))",
          700: "#b91c1c",
          800: "#991b1b",
          900: "#7f1d1d",
        },

        // Yellow palette (Figma 100-600, Tailwind defaults for 50, 700-950)
        yellow: {
          50: "#fefce8",
          100: "rgb(var(--color-yellow-100))",
          200: "rgb(var(--color-yellow-200))",
          300: "rgb(var(--color-yellow-300))",
          400: "rgb(var(--color-yellow-400))",
          500: "rgb(var(--color-yellow-500))",
          600: "rgb(var(--color-yellow-600))",
          700: "#a16207",
          800: "#854d0e",
          900: "#713f12",
        },

        // Blue palette (Figma 100-600, Tailwind defaults for 50, 700-950)
        blue: {
          50: "#eff6ff",
          100: "rgb(var(--color-blue-100))",
          200: "rgb(var(--color-blue-200))",
          300: "rgb(var(--color-blue-300))",
          400: "rgb(var(--color-blue-400))",
          500: "rgb(var(--color-blue-500))",
          600: "rgb(var(--color-blue-600))",
          700: "#1d4ed8",
          800: "#1e40af",
          900: "#1e3a5f",
        },

        // Purple palette (Figma 100-600, Tailwind defaults for 50, 700-950)
        purple: {
          50: "#faf5ff",
          100: "rgb(var(--color-purple-100))",
          200: "rgb(var(--color-purple-200))",
          300: "rgb(var(--color-purple-300))",
          400: "rgb(var(--color-purple-400))",
          500: "rgb(var(--color-purple-500))",
          600: "rgb(var(--color-purple-600))",
          700: "#6d28d9",
          800: "#5b21b6",
          900: "#4c1d95",
        },

        // Green palette (Figma 100-600, Tailwind defaults for 50, 700-950)
        green: {
          DEFAULT: "rgb(var(--color-aqua-500))",
          50: "#f0fdf4",
          100: "rgb(var(--color-green-100))",
          200: "rgb(var(--color-green-200))",
          300: "rgb(var(--color-green-300))",
          400: "rgb(var(--color-green-400))",
          500: "rgb(var(--color-green-500))",
          600: "rgb(var(--color-green-600))",
          700: "#15803d",
          800: "#166534",
          900: "#14532d",
        },

        // Slate palette (preserve Tailwind default for tw-*-slate-* usage)
        slate: {
          50: "#f8fafc",
          100: "#f1f5f9",
          200: "#e2e8f0",
          300: "#cbd5e1",
          400: "#94a3b8",
          500: "#64748b",
          600: "#475569",
          700: "#334155",
          800: "#1e293b",
          900: "#0f172a",
        },
      },
      backgroundImage: {
        "aqua-gradient":
          "linear-gradient(to right, white, rgb(var(--color-aqua-100)))",
      },
      transitionDuration: {
        0: "0ms",
        2000: "2000ms",
        5000: "5000ms",
      },
      transitionDelay: {
        0: "0ms",
        2000: "2000ms",
        5000: "5000ms",
      },
      keyframes: {
        "rating-select": {
          "0%": { transform: "scale(1)" },
          "40%": { transform: "scale(1.5)" },
          "100%": { transform: "scale(1)" },
        },
      },
      animation: {
        "rating-select": "rating-select 450ms ease-out",
      },
    },
  },
  variants: {
    extend: {},
  },
  plugins: [tailwindTypography, containerQueries],
};
